import React, { useState } from "react";
import { View, StyleSheet, TextInput, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface RemarkModalProps {
  title: string;
  description?: string;
  initialValue?: string;
  required?: boolean;
  onSave: (value: string) => void;
  onCancel: () => void;
}

export function RemarkModal({
  title,
  description,
  initialValue = "",
  required = false,
  onSave,
  onCancel,
}: RemarkModalProps) {
  const { colors } = useTheme();
  const [value, setValue] = useState(initialValue);

  const isSaveDisabled = required && !value.trim();

  return (
    <View style={[styles.container, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <ThemedText type="title" style={[styles.title, { color: colors.text }]}>
        {title}
      </ThemedText>

      {description && (
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>{description}</ThemedText>
      )}

      <TextInput
        style={[
          styles.input,
          {
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
            borderColor: colors.border,
          },
        ]}
        value={value}
        onChangeText={setValue}
        placeholder="Enter a remark..."
        placeholderTextColor={colors.placeholder}
        multiline
        numberOfLines={4}
        textAlignVertical="top"
        autoFocus
      />

      <View style={styles.actions}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Save"
          onPress={() => onSave(value)}
          enabled={!isSaveDisabled}
          style={[styles.actionButton, { backgroundColor: colors.primary, opacity: isSaveDisabled ? 0.4 : 1 }]}
          textStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 400,
    minWidth: 300,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 24,
    boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.25)",
    elevation: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    marginBottom: 20,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  actions: {
    flexDirection: "row",
    gap: 12,
  },
  actionButton: {
    flex: 1,
    minHeight: 44,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 6,
    borderWidth: 1,
  },
});
